// custom typefaces
import "typeface-montserrat";
import "typeface-merriweather";
// normalize CSS across browsers
import "./src/normalize.css";
// custom CSS styles
import "./src/style.scss";

// Highlighting for code blocks
import "prismjs/themes/prism.css";


// Track page navigation for referrer information
let previousPage = '';

// Function to detect traffic source based on referrer
function detectTrafficSource(referrer) {
    if (!referrer) return '';
    
    const referrerDomain = new URL(referrer).hostname.toLowerCase();
    
    // Search engines
    if (referrerDomain.includes('google.')) return 'Google';
    if (referrerDomain.includes('bing.')) return 'Bing';
    if (referrerDomain.includes('yahoo.')) return 'Yahoo';
    if (referrerDomain.includes('duckduckgo.')) return 'DuckDuckGo';
    if (referrerDomain.includes('baidu.')) return 'Baidu';
    if (referrerDomain.includes('yandex.')) return 'Yandex';
    
    // Social media
    if (referrerDomain.includes('facebook.')) return 'Facebook';
    if (referrerDomain.includes('instagram.')) return 'Instagram';
    if (referrerDomain.includes('twitter.') || referrerDomain.includes('x.com')) return 'Twitter/X';
    if (referrerDomain.includes('linkedin.')) return 'LinkedIn';
    if (referrerDomain.includes('youtube.')) return 'YouTube';
    if (referrerDomain.includes('tiktok.')) return 'TikTok';
    if (referrerDomain.includes('pinterest.')) return 'Pinterest';
    if (referrerDomain.includes('snapchat.')) return 'Snapchat';
    
    // Other common sources
    if (referrerDomain.includes('reddit.')) return 'Reddit';
    if (referrerDomain.includes('tripadvisor.')) return 'TripAdvisor';
    if (referrerDomain.includes('booking.')) return 'Booking.com';
    if (referrerDomain.includes('expedia.')) return 'Expedia';
    if (referrerDomain.includes('agoda.')) return 'Agoda';
    
    // Return the domain name if no specific match
    return referrerDomain;
}

// Function to detect organic source based on referrer
function detectOrganicSource(referrer) {
    if (!referrer) return '';
    
    const referrerDomain = new URL(referrer).hostname.toLowerCase();
    
    // Check if it's a search engine (organic traffic)
    if (referrerDomain.includes('google.') || 
        referrerDomain.includes('bing.') || 
        referrerDomain.includes('yahoo.') || 
        referrerDomain.includes('duckduckgo.') || 
        referrerDomain.includes('baidu.') || 
        referrerDomain.includes('yandex.')) {
        return 'Search Engine';
    }
    
    // Check if it's social media
    if (referrerDomain.includes('facebook.') || 
        referrerDomain.includes('instagram.') || 
        referrerDomain.includes('twitter.') || 
        referrerDomain.includes('x.com') || 
        referrerDomain.includes('linkedin.') || 
        referrerDomain.includes('youtube.') || 
        referrerDomain.includes('tiktok.') || 
        referrerDomain.includes('pinterest.') || 
        referrerDomain.includes('snapchat.')) {
        return 'Social Media';
    }
    
    // Check if it's a travel/booking site
    if (referrerDomain.includes('tripadvisor.') || 
        referrerDomain.includes('booking.') || 
        referrerDomain.includes('expedia.') || 
        referrerDomain.includes('agoda.')) {
        return 'Travel Site';
    }
    
    // Check if it's a forum/community
    if (referrerDomain.includes('reddit.')) {
        return 'Forum/Community';
    }
    
    // Default to "Direct" for other external sources
    return 'Direct';
}

export const onRouteUpdate = ({ location }) => {
    // Store the previous page URL before navigating to a new page
    if (previousPage && previousPage !== location.pathname) {
        sessionStorage.setItem('previous_page', previousPage);
        console.log('Previous page stored:', previousPage);
    }
    previousPage = location.pathname;

    // Only capture UTM/gclid params when they are present in the URL.
    // Never overwrite with empty values — internal navigation must not
    // corrupt the session's first-touch attribution.
    const params = new URLSearchParams(location.search || '');
    const utmSource = params.get('utm_source') || '';
    const utmMedium = params.get('utm_medium') || '';
    const utmCampaign = params.get('utm_campaign') || '';
    const utmTerm = params.get('utm_term') || '';
    const utmContent = params.get('utm_content') || '';
    const trafficSource = params.get('traffic_source') || '';
    const organic = params.get('organic') || '';
    const referrer = params.get('referrer') || '';
    const gclid = params.get('gclid') || '';

    if (utmSource) sessionStorage.setItem('utm_source', utmSource);
    if (utmMedium) sessionStorage.setItem('utm_medium', utmMedium);
    if (utmCampaign) sessionStorage.setItem('utm_campaign', utmCampaign);
    if (utmTerm) sessionStorage.setItem('utm_term', utmTerm);
    if (utmContent) sessionStorage.setItem('utm_content', utmContent);
    if (trafficSource) sessionStorage.setItem('traffic_source', trafficSource);
    if (organic) sessionStorage.setItem('organic', organic);
    if (referrer) sessionStorage.setItem('referrer', referrer);
    if (gclid) sessionStorage.setItem('gclid', gclid);
};

export const onInitialClientRender = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const utmSource = searchParams.get('utm_source') || '';
    const utmMedium = searchParams.get('utm_medium') || '';
    const utmCampaign = searchParams.get('utm_campaign') || '';
    const utmTerm = searchParams.get('utm_term') || '';
    const utmContent = searchParams.get('utm_content') || '';
    const trafficSource = searchParams.get('traffic_source') || '';
    const organic = searchParams.get('organic') || '';
    const referrer = searchParams.get('referrer') || '';
    const gclid = searchParams.get('gclid') || '';

    // Capture the browser's natural referrer (previous page)
    const documentReferrer = document.referrer || '';

    // Store UTM/gclid parameters — sessionStorage is intentional so
    // attribution resets on each new browser session, preventing stale
    // data from a previous visit polluting new direct-traffic enquiries.
    if (utmSource || utmMedium || utmCampaign || utmTerm || utmContent || trafficSource || organic || referrer || gclid) {
        if (utmSource) sessionStorage.setItem('utm_source', utmSource);
        if (utmMedium) sessionStorage.setItem('utm_medium', utmMedium);
        if (utmCampaign) sessionStorage.setItem('utm_campaign', utmCampaign);
        if (utmTerm) sessionStorage.setItem('utm_term', utmTerm);
        if (utmContent) sessionStorage.setItem('utm_content', utmContent);
        if (trafficSource) sessionStorage.setItem('traffic_source', trafficSource);
        if (organic) sessionStorage.setItem('organic', organic);
        if (referrer) sessionStorage.setItem('referrer', referrer);
        if (gclid) sessionStorage.setItem('gclid', gclid);
    }

    // Auto-detect traffic source and organic source from UTM parameters if not provided
    if (!trafficSource && utmSource) {
        const utmTrafficSource = utmSource.charAt(0).toUpperCase() + utmSource.slice(1);
        sessionStorage.setItem('traffic_source', utmTrafficSource);
        console.log('Traffic source auto-detected from UTM:', utmTrafficSource);
    }

    if (!organic && utmMedium) {
        let utmOrganicSource = '';
        switch (utmMedium.toLowerCase()) {
            case 'organic':
            case 'cpc':
            case 'ppc':
                utmOrganicSource = 'Search Engine';
                break;
            case 'social':
            case 'facebook':
            case 'instagram':
            case 'twitter':
            case 'linkedin':
            case 'youtube':
            case 'tiktok':
            case 'pinterest':
            case 'snapchat':
                utmOrganicSource = 'Social Media';
                break;
            case 'email':
                utmOrganicSource = 'Email';
                break;
            case 'referral':
                utmOrganicSource = 'Referral';
                break;
            case 'display':
            case 'banner':
                utmOrganicSource = 'Display';
                break;
            default:
                utmOrganicSource = utmMedium.charAt(0).toUpperCase() + utmMedium.slice(1);
        }
        sessionStorage.setItem('organic', utmOrganicSource);
        console.log('Organic source auto-detected from UTM:', utmOrganicSource);
    }

    // Store document referrer if it exists (including same-domain)
    if (documentReferrer) {
        sessionStorage.setItem('document_referrer', documentReferrer);
        console.log('Document referrer captured:', documentReferrer);

        // Auto-detect traffic and organic sources based on referrer
        const detectedTrafficSource = detectTrafficSource(documentReferrer);
        const detectedOrganicSource = detectOrganicSource(documentReferrer);

        if (!trafficSource && detectedTrafficSource) {
            sessionStorage.setItem('traffic_source', detectedTrafficSource);
            console.log('Traffic source auto-detected:', detectedTrafficSource);
        }

        if (!organic && detectedOrganicSource) {
            sessionStorage.setItem('organic', detectedOrganicSource);
            console.log('Organic source auto-detected:', detectedOrganicSource);
        }
    } else if (previousPage) {
        // Fallback to previous page if no document referrer available
        sessionStorage.setItem('document_referrer', previousPage);
        console.log('Document referrer fallback to previous page:', previousPage);
    }

    // Initialize previous page tracking
    previousPage = window.location.pathname;
};
